import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import { Skeleton } from "@mui/material";
import API from "../../API/newIndex";
import { ArrowLeft, Trash2, Edit, Camera } from "feather-icons-react/build/IconComponents";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { parentNavigate } from "../../controllers/ParentNavigate";
import Notification from "../Notification";
import {
  ButtonComponent,
  DetailsComponent,
  InputComponent,
  SelectComponent,
} from "@shopeasy-dashboard-ui/ui-library";
import TextareaComponent from "../FormInput/TextareaComponent";
import {
  getVendorValidationSchema,
  validateTabFields,
  getFirstTabWithError,
} from "./vendorValidation";

const slugify = (text) =>
  text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-");

const VendorForm = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id && id !== "new");

  const [activeTab, setActiveTab] = useState("general"); // 'general', 'commission', 'social'
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [initialFormValues, setInitialFormValues] = useState(null);

  const profileInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const [notification, setNotification] = useState({
    show: false,
    message: "",
    severity: "info",
  });

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      phone: "",
      country_code: "+91",
      profile_picture: "",
      shop_name: "",
      shop_slug: "",
      vendor_logo: "",
      vendor_banner: "",
      vendor_tagline: "",
      vendor_address: "",
      commission_type: "percentage",
      commission: 0,
      social_links: {
        twitter: "",
        facebook: "",
        instagram: "",
        linkedin: "",
      },
    },
    validationSchema: getVendorValidationSchema(isEditMode),
    enableReinitialize: true,
    onSubmit: async (values) => {
      setError("");
      setSubmitting(true);

      try {
        const payload = {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          phone: values.phone,
          country_code: values.country_code,
          profile_picture: values.vendor_logo || values.profile_picture || "",
          shop_name: values.shop_name,
          shop_slug: values.shop_slug || slugify(values.shop_name),
          vendor_logo: values.vendor_logo || values.profile_picture || "",
          vendor_banner: values.vendor_banner,
          vendor_tagline: values.vendor_tagline,
          vendor_address: values.vendor_address,
          commission_type: values.commission_type,
          commission: Number(values.commission) || 0,
          social_links: values.social_links || {},
        };

        if (!isEditMode) {
          payload.password = values.password;
        }

        if (isEditMode) {
          await API({
            url: `/admin/vendor/update/${id}`,
            method: "put",
            data: payload,
          });
          setNotification({
            show: true,
            message: "Vendor updated successfully",
            severity: "success",
          });
        } else {
          await API({
            url: "/admin/vendor/create",
            method: "post",
            data: payload,
          });
          setNotification({
            show: true,
            message: "Vendor created successfully! Verification email sent to vendor.",
            severity: "success",
          });
        }

        setTimeout(() => {
          parentNavigate("/vendors");
        }, 1200);
      } catch (err) {
        console.error("Save vendor error:", err);
        setError(err?.response?.data?.error || err?.response?.data?.message || err.message || "Failed to save vendor details.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fetchVendorDetails = useCallback(async () => {
    if (!isEditMode) {
      setLoading(true);
      try {
        const res = await API({
          url: "/admin/vendor/config",
          method: "get",
        });
        const config = res?.data?.data || res?.data?.config || res?.data || {};
        if (config.commission_type) {
          formik.setFieldValue("commission_type", config.commission_type);
        }
        if (config.commission !== undefined && config.commission !== null) {
          formik.setFieldValue("commission", config.commission);
        }
      } catch (err) {
        console.error("Error loading vendor config defaults:", err);
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      const res = await API({
        url: `/admin/vendor/detail/${id}`,
        method: "get",
      });
      if (res?.data?.data) {
        const v = res.data.data;
        let socialData = v.social_links || v.social_app;
        if (typeof socialData === "string") {
          try {
            socialData = JSON.parse(socialData);
          } catch (e) {
            socialData = {};
          }
        }
        if (!socialData || typeof socialData !== "object") {
          socialData = {};
        }

        const logoValue = v.vendor_logo || v.logo || v.profile_picture || v.avatar || "";
        const bannerValue = v.vendor_banner || v.banner || "";

        let addressValue = v.vendor_address || v.business_address || "";
        if (typeof addressValue === "string") {
          try {
            const parsed = JSON.parse(addressValue);
            if (typeof parsed === "object" && parsed !== null) {
              const parts = [
                parsed.street,
                parsed.street2,
                parsed.city,
                parsed.state,
                parsed.zip,
                parsed.country,
              ].filter(Boolean);
              if (parts.length > 0) addressValue = parts.join(", ");
            }
          } catch (e) { }
        } else if (typeof addressValue === "object" && addressValue !== null) {
          const parts = [
            addressValue.street,
            addressValue.street2,
            addressValue.city,
            addressValue.state,
            addressValue.zip,
            addressValue.country,
          ].filter(Boolean);
          if (parts.length > 0) addressValue = parts.join(", ");
        }

        const loadedValues = {
          first_name: v.first_name || (v.name ? v.name.split(" ")[0] : ""),
          last_name: v.last_name || (v.name ? v.name.split(" ").slice(1).join(" ") : ""),
          email: v.email || "",
          password: "",
          phone: v.phone || "",
          country_code: v.country_code || "+91",
          profile_picture: logoValue,
          vendor_logo: logoValue,
          shop_name: v.shop_name || "",
          shop_slug: v.shop_slug || slugify(v.shop_name || ""),
          vendor_banner: bannerValue,
          vendor_tagline: v.vendor_tagline || v.tagline || "",
          vendor_address: addressValue,
          commission_type: v.commission_type || "percentage",
          commission: v.commission ?? v.commission_rate ?? 0,
          social_links: {
            twitter: socialData?.twitter || v.twitter || "",
            facebook: socialData?.facebook || v.facebook || "",
            instagram: socialData?.instagram || v.instagram || "",
            linkedin: socialData?.linkedin || v.linkedin || "",
          },
        };

        setInitialFormValues(loadedValues);
        formik.setValues(loadedValues);
      }
    } catch (err) {
      console.error("Error loading vendor details:", err);
      setNotification({
        show: true,
        message: err?.response?.data?.message || err.message || "Failed to load vendor details",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [id, isEditMode]);

  useEffect(() => {
    fetchVendorDetails();
  }, [fetchVendorDetails]);

  const isFormChanged = React.useMemo(() => {
    if (!isEditMode) return true;
    if (!initialFormValues) return false;
    return JSON.stringify(formik.values) !== JSON.stringify(initialFormValues);
  }, [isEditMode, initialFormValues, formik.values]);

  const handleShopNameChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue("shop_name", value);
    if (!isEditMode) {
      formik.setFieldValue("shop_slug", slugify(value));
    }
  };

  const handleNestedChange = (parentKey, childKey, value) => {
    formik.setFieldValue(`${parentKey}.${childKey}`, value);
  };

  const [uploadingField, setUploadingField] = useState(null);

  const handleImageUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;

    e.target.value = "";

    const maxMb = field === "vendor_banner" ? 5 : 2;
    const MAX_SIZE = maxMb * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setNotification({
        show: true,
        message: `${field === "vendor_banner" ? "Banner" : "Profile picture"} size must be less than ${maxMb}MB`,
        severity: "error",
      });
      return;
    }

    setUploadingField(field);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "vendor");

      const res = await API({
        url: "/admin/fileupload/upload",
        method: "post",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      const uploadedUrl =
        (Array.isArray(res?.data?.data) ? res.data.data[0] : null) ||
        res?.data?.url ||
        (typeof res?.data?.data === "string" ? res.data.data : null) ||
        res?.data?.data?.url;

      if (uploadedUrl) {
        formik.setFieldValue(field, uploadedUrl);
        if (field === "vendor_logo" || field === "profile_picture") {
          formik.setFieldValue("vendor_logo", uploadedUrl);
          formik.setFieldValue("profile_picture", uploadedUrl);
        }
        setNotification({
          show: true,
          message: "Image uploaded successfully",
          severity: "success",
        });
      }
    } catch (err) {
      console.error("Image upload failed:", err);
      setNotification({
        show: true,
        message: err?.response?.data?.message || err.message || "Failed to upload image",
        severity: "error",
      });
    } finally {
      setUploadingField(null);
    }
  };

  const scrollToFirstError = (errors) => {
    const firstField = Object.keys(errors)[0];
    if (firstField) {
      const element = document.querySelector(`[name="${firstField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus?.();
      }
    }
  };

  const handleNext = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    let targetTab = "general";
    if (activeTab === "general") targetTab = "commission";
    else if (activeTab === "commission") targetTab = "social";

    const tabErrors = await validateTabFields(activeTab, formik.values, isEditMode);

    if (Object.keys(tabErrors).length > 0) {
      const touchedFields = {};
      Object.keys(tabErrors).forEach((key) => {
        touchedFields[key] = true;
      });
      formik.setTouched({ ...formik.touched, ...touchedFields });
      formik.setErrors({ ...formik.errors, ...tabErrors });
      scrollToFirstError(tabErrors);
      return;
    }

    setActiveTab(targetTab);
  };

  const handlePrev = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (activeTab === "social") setActiveTab("commission");
    else if (activeTab === "commission") setActiveTab("general");
  };

  const handleTabClick = async (targetTab) => {
    if (activeTab === targetTab) return;

    const tabOrder = ["general", "commission", "social"];
    const currentIndex = tabOrder.indexOf(activeTab);
    const targetIndex = tabOrder.indexOf(targetTab);

    if (targetIndex > currentIndex) {
      const tabErrors = await validateTabFields(activeTab, formik.values, isEditMode);

      if (Object.keys(tabErrors).length > 0) {
        const touchedFields = {};
        Object.keys(tabErrors).forEach((key) => {
          touchedFields[key] = true;
        });
        formik.setTouched({ ...formik.touched, ...touchedFields });
        formik.setErrors({ ...formik.errors, ...tabErrors });
        scrollToFirstError(tabErrors);
        return;
      }
    }

    setActiveTab(targetTab);
  };

  const handleFinalSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const allErrors = await formik.validateForm();

    if (allErrors && Object.keys(allErrors).length > 0) {
      const touchedFields = {};
      Object.keys(allErrors).forEach((key) => {
        touchedFields[key] = true;
      });
      formik.setTouched(touchedFields);

      const firstTab = getFirstTabWithError(allErrors);
      if (firstTab) {
        setActiveTab(firstTab);
      }
      setTimeout(() => {
        scrollToFirstError(allErrors);
      }, 100);
      return;
    }

    formik.handleSubmit();
  };

  if (loading) {
    return (
      <div className="h-auto overflow-y-auto pb-[130px] pt-5 px-5">
        <div className="w-full lg:w-[70%] xl:w-[60%] mx-auto space-y-4">
          <div className="flex items-center space-x-2 page-header-pb">
            <Skeleton variant="circular" width={28} height={28} />
            <div>
              <Skeleton variant="text" width={140} height={16} />
              <Skeleton variant="text" width={180} height={28} />
            </div>
          </div>
          <div className="secondary-bg-color rounded-[8px] border main-border-color shadow-sm p-6 space-y-4">
            <Skeleton variant="rectangular" width="100%" height={48} className="rounded-lg" />
            <Skeleton variant="rectangular" width="100%" height={48} className="rounded-lg" />
            <Skeleton variant="rectangular" width="100%" height={120} className="rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  const tabClass = (isActive) =>
    `pb-2.5 px-3 text-xs sm:text-sm font-medium border-b-2 transition-colors duration-200 cursor-pointer ${isActive
      ? "border-[var(--btn-bg-primary)] text-[var(--primary-text-color)] font-semibold"
      : "border-transparent text-[var(--table-text-color)] hover:text-[var(--primary-text-color)]"
    }`;

  return (
    <>
      {notification.show && (
        <Notification
          message={notification.message}
          close={() => setNotification((prev) => ({ ...prev, show: false }))}
          severity={notification.severity}
        />
      )}

      <div className="h-auto overflow-y-auto pb-[130px] pt-5 px-5">
        {/* Navigation Header */}
        <div className="w-full lg:w-[70%] xl:w-[60%] mx-auto">
          <div className="flex items-center space-x-2 page-header-pb">
            <button
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/vendors");
              }}
              className="back-arrow"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: "Dashboard", path: "/dashboard" },
                  { name: "Vendors", path: "/vendors" },
                  {
                    name: isEditMode ? "Update Vendor" : "Add Vendor",
                    path: isEditMode ? `/vendors/${id}` : "/vendors/add",
                  },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">
                {isEditMode ? "Update Vendor" : "Add Vendor"}
              </h4>
            </div>
          </div>

          {/* Sub Navigation Tabs */}
          <div className="flex items-center gap-6 border-b main-border-color mb-4">
            <button
              type="button"
              onClick={() => handleTabClick("general")}
              className={tabClass(activeTab === "general")}
            >
              General Info
            </button>
            <button
              type="button"
              onClick={() => handleTabClick("commission")}
              className={tabClass(activeTab === "commission")}
            >
              Commission
            </button>
            <button
              type="button"
              onClick={() => handleTabClick("social")}
              className={tabClass(activeTab === "social")}
            >
              Social Options
            </button>
          </div>
        </div>

        {/* Main Form Content */}
        <form
          onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
          method="POST"
          onSubmit={handleFinalSubmit}
          className="w-full lg:w-[70%] xl:w-[60%] mx-auto flex flex-col gap-3"
        >
          {error && (
            <div className="p-3 text-xs bg-rose-50 text-rose-600 rounded-lg border border-rose-200 font-medium">
              {error}
            </div>
          )}

          {/* TAB 1: General Info */}
          {activeTab === "general" && (
            <DetailsComponent
              open={true}
              hasHr={false}
              showChevron={false}
              className="!pt-5 sm:!pt-[25px]"
            >
              {/* Media Uploads: Vendor Logo & Vendor Banner */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Vendor Logo Upload */}
                <div className="w-full">
                  <label className="block text-[13px] font-medium tracking-tight mb-1">
                    Vendor Logo
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 rounded-full bg-[var(--primary-bg-color)] border main-border-color flex items-center justify-center overflow-hidden shrink-0">
                      {formik.values.vendor_logo || formik.values.profile_picture ? (
                        <img
                          src={formik.values.vendor_logo || formik.values.profile_picture}
                          alt="Vendor Logo"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Camera className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        ref={profileInputRef}
                        onChange={(e) => handleImageUpload(e, "vendor_logo")}
                        accept="image/*"
                        className="hidden"
                      />
                      <ButtonComponent
                        type="button"
                        variant="outline"
                        size="sm"
                        loading={uploadingField === "vendor_logo" || uploadingField === "profile_picture"}
                        icon={<Edit className="w-3.5 h-3.5" />}
                        onClick={() => profileInputRef.current?.click()}
                      >
                        {formik.values.vendor_logo || formik.values.profile_picture ? "Change" : "Upload"}
                      </ButtonComponent>
                      {(formik.values.vendor_logo || formik.values.profile_picture) && (
                        <ButtonComponent
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            formik.setFieldValue("vendor_logo", "");
                            formik.setFieldValue("profile_picture", "");
                          }}
                          className="!p-2"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </ButtonComponent>
                      )}
                    </div>
                  </div>
                </div>

                {/* Vendor Banner Upload */}
                <div className="w-full">
                  <label className="block text-[13px] font-medium tracking-tight mb-1">
                    Vendor Banner
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="w-28 h-16 rounded-lg bg-[var(--primary-bg-color)] border main-border-color flex items-center justify-center overflow-hidden shrink-0">
                      {formik.values.vendor_banner ? (
                        <img
                          src={formik.values.vendor_banner}
                          alt="Vendor Banner"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-gray-400">
                          <Camera className="w-5 h-5" />
                          <span className="text-[9px] text-gray-400 mt-0.5">Banner</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        ref={bannerInputRef}
                        onChange={(e) => handleImageUpload(e, "vendor_banner")}
                        accept="image/*"
                        className="hidden"
                      />
                      <ButtonComponent
                        type="button"
                        variant="outline"
                        size="sm"
                        loading={uploadingField === "vendor_banner"}
                        icon={<Edit className="w-3.5 h-3.5" />}
                        onClick={() => bannerInputRef.current?.click()}
                      >
                        {formik.values.vendor_banner ? "Change" : "Upload"}
                      </ButtonComponent>
                      {formik.values.vendor_banner && (
                        <ButtonComponent
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => formik.setFieldValue("vendor_banner", "")}
                          className="!p-2"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </ButtonComponent>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <InputComponent
                  label="First Name"
                  name="first_name"
                  required={true}
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.first_name && formik.errors.first_name
                      ? formik.errors.first_name
                      : ""
                  }
                  placeholder="e.g. John"
                />
                <InputComponent
                  label="Last Name"
                  name="last_name"
                  required={true}
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.last_name && formik.errors.last_name
                      ? formik.errors.last_name
                      : ""
                  }
                  placeholder="e.g. Doe"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <InputComponent
                  label="Email Address"
                  name="email"
                  type="email"
                  required={true}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.email && formik.errors.email
                      ? formik.errors.email
                      : ""
                  }
                  placeholder="vendor@example.com"
                />
                <InputComponent
                  label="Phone Number"
                  name="phone"
                  required={true}
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.phone && formik.errors.phone
                      ? formik.errors.phone
                      : ""
                  }
                  placeholder="e.g. 9876543210"
                />
              </div>

              {!isEditMode && (
                <div className="mb-4">
                  <InputComponent
                    label="Account Password"
                    name="password"
                    type="password"
                    required={true}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.password && formik.errors.password
                        ? formik.errors.password
                        : ""
                    }
                    placeholder="Enter vendor login password"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <InputComponent
                  label="Shop Name"
                  name="shop_name"
                  required={true}
                  value={formik.values.shop_name}
                  onChange={handleShopNameChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.shop_name && formik.errors.shop_name
                      ? formik.errors.shop_name
                      : ""
                  }
                  placeholder="e.g. Acme Superstore"
                />
                <InputComponent
                  label="Shop Handle / Slug"
                  name="shop_slug"
                  required={true}
                  value={formik.values.shop_slug}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.shop_slug && formik.errors.shop_slug
                      ? formik.errors.shop_slug
                      : ""
                  }
                  placeholder="acme-superstore"
                />
              </div>

              <div className="mb-4">
                <InputComponent
                  label="Vendor Tagline"
                  name="vendor_tagline"
                  value={formik.values.vendor_tagline}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.vendor_tagline && formik.errors.vendor_tagline
                      ? formik.errors.vendor_tagline
                      : ""
                  }
                  placeholder="e.g. Quality products at the best prices"
                />
              </div>

              <div className="mb-2">
                <TextareaComponent
                  label="Vendor Address"
                  name="vendor_address"
                  rows={3}
                  value={formik.values.vendor_address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter full vendor business address..."
                />
              </div>
            </DetailsComponent>
          )}

          {/* TAB 2: Commission */}
          {activeTab === "commission" && (
            <DetailsComponent
              open={true}
              hasHr={false}
              showChevron={false}
              className="!pt-5 sm:!pt-[25px]"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
                <SelectComponent
                  label="Commission Type"
                  required={true}
                  name="commission_type"
                  value={formik.values.commission_type}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.commission_type && formik.errors.commission_type
                      ? formik.errors.commission_type
                      : ""
                  }
                  options={[
                    { value: "percentage", label: "Percentage (%)" },
                    { value: "fixed", label: "Fixed Rate ($)" },
                    { value: "category", label: "Category Based" },
                  ]}
                />

                <InputComponent
                  label={`Commission Rate (${formik.values.commission_type === "percentage" ? "%" : "$"})`}
                  name="commission"
                  type="number"
                  step="0.01"
                  required={true}
                  value={formik.values.commission}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.commission && formik.errors.commission
                      ? formik.errors.commission
                      : ""
                  }
                  placeholder="0"
                />
              </div>
            </DetailsComponent>
          )}

          {/* TAB 3: Social Profiles */}
          {activeTab === "social" && (
            <DetailsComponent
              open={true}
              hasHr={false}
              showChevron={false}
              className="!pt-5 sm:!pt-[25px]"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
                <InputComponent
                  label="Twitter Profile"
                  name="social_links.twitter"
                  value={formik.values.social_links?.twitter || ""}
                  onChange={(e) => handleNestedChange("social_links", "twitter", e.target.value)}
                  onBlur={formik.handleBlur}
                  placeholder="https://twitter.com/username"
                />
                <InputComponent
                  label="Facebook Profile"
                  name="social_links.facebook"
                  value={formik.values.social_links?.facebook || ""}
                  onChange={(e) => handleNestedChange("social_links", "facebook", e.target.value)}
                  onBlur={formik.handleBlur}
                  placeholder="https://facebook.com/username"
                />
                <InputComponent
                  label="Instagram Profile"
                  name="social_links.instagram"
                  value={formik.values.social_links?.instagram || ""}
                  onChange={(e) => handleNestedChange("social_links", "instagram", e.target.value)}
                  onBlur={formik.handleBlur}
                  placeholder="https://instagram.com/username"
                />
                <InputComponent
                  label="LinkedIn Profile"
                  name="social_links.linkedin"
                  value={formik.values.social_links?.linkedin || ""}
                  onChange={(e) => handleNestedChange("social_links", "linkedin", e.target.value)}
                  onBlur={formik.handleBlur}
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
            </DetailsComponent>
          )}
        </form>

        {/* Bottom Fixed Action Save Bar */}
        <div className="flex items-center shadow fixed justify-between w-full bottom-[0px] z-50 secondary-bg-color border-t main-border-color p-3 left-0">
          <div className="w-[90%] flex items-center justify-between lg:w-[70%] xl:w-[60%] mx-auto">
            <ButtonComponent
              variant="link"
              icon={<ArrowLeft className="w-3" />}
              onClick={(e) => {
                e.preventDefault();
                parentNavigate("/vendors");
              }}
              className="text-primary"
            >
              Go To Vendors
            </ButtonComponent>

            <div className="flex items-center gap-2.5">
              {activeTab !== "general" && (
                <ButtonComponent
                  type="button"
                  variant="outline"
                  size="md"
                  onClick={handlePrev}
                >
                  Previous
                </ButtonComponent>
              )}

              {activeTab !== "social" ? (
                <ButtonComponent
                  type="button"
                  variant="primary"
                  size="md"
                  onClick={handleNext}
                >
                  Next
                </ButtonComponent>
              ) : (
                <ButtonComponent
                  type="button"
                  variant="primary"
                  size="md"
                  loading={submitting}
                  disabled={isEditMode ? !isFormChanged : false}
                  onClick={handleFinalSubmit}
                >
                  {isEditMode ? "Save Changes" : "Create Vendor"}
                </ButtonComponent>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VendorForm;
